import type { AgentFocus } from '@tunasong/agent-context'
import { nlpApi, startAgentTurn } from '@tunasong/redux'
import type { AgentStreamPayload, TunaThunkDispatch } from '@tunasong/redux'
import type { VirtualElement } from '@tunasong/ui-lib'
import { useSyncExternalStore } from 'react'

const mintId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

export interface StartInlineTurnInput {
  agentId: string
  message: string
  focus?: AgentFocus
  trigger?: NonNullable<AgentStreamPayload['trigger']>
}

/**
 * Fire an interactive in-context agent turn (fire-and-forget). Marks the turn
 * `interactive` so the server returns the edit inline, and sends a fresh
 * exchange id, so the server also persists the proposal and saves the question
 * with the answer (GH #2677). Returns the ids the inline surfaces subscribe on;
 * the turn id lets a dismiss cancel the turn.
 */
export const startInlineAgentTurn = (
  deps: { dispatch: TunaThunkDispatch },
  input: StartInlineTurnInput
): { agentId: string; requestId: string; turnId: string; exchangeId: string } => {
  const requestId = mintId()
  const turnId = mintId()
  const exchangeId = mintId()
  const params: AgentStreamPayload = {
    agentId: input.agentId,
    message: input.message,
    focus: input.focus,
    trigger: input.trigger ?? { type: 'chat' },
    interactive: true,
    requestId,
    turnId,
    exchangeId,
  }
  startAgentTurn(deps.dispatch, params)
  return { agentId: input.agentId, requestId, turnId, exchangeId }
}

/** The turn the inline surfaces (ghost / answer note) currently render. */
export interface ActiveInlineTurn {
  agentId: string
  requestId: string
  /** The turn's id, so a dismiss can cancel it while it still runs (GH #2677) */
  turnId?: string
  /** Placement anchor for the surface — a live DOM node or a virtual (rect-only) element. */
  anchorEl: HTMLElement | VirtualElement | null
  /**
   * When true, the host renders ONLY an editable ghost — the caller (e.g. a
   * streaming palette command with its own response popup) already shows the
   * answer, so a transient answer note would double it.
   */
  suppressAnswerNote?: boolean
}

type Listener = () => void

const listeners = new Set<Listener>()
let active: ActiveInlineTurn | null = null

/**
 * Tiny module-scoped store (mirrors `scale-picker/picker-bus.ts`) tracking
 * which inline agent turn — if any — the interactive surfaces should render.
 *
 * `startInlineAgentTurn` deliberately does NOT call `setActiveInlineTurn`
 * itself: only the caller holds the block's `anchorEl` (a DOM node or Popper
 * virtual element resolved from the triggering selection/block), so wiring
 * the store is left to those callers.
 */
export const setActiveInlineTurn = (turn: ActiveInlineTurn): void => {
  active = turn
  for (const listener of listeners) {
    listener()
  }
}

/** Clear the active turn — called on resolve (ghost accept/reject) or dismiss (answer note). */
export const clearActiveInlineTurn = (): void => {
  active = null
  for (const listener of listeners) {
    listener()
  }
}

/** The active inline turn, outside React (tests, event handlers) */
export const getActiveInlineTurn = (): ActiveInlineTurn | null => active

/**
 * Dismiss the active inline turn. While it still runs, cancel it too: closing an answer otherwise left the turn
 * running and billed (GH #2677). The server checks the cancel marker between steps and persists nothing for a
 * cancelled turn.
 */
export const dismissInlineTurn = (deps: { dispatch: TunaThunkDispatch }, { pending }: { pending: boolean }) => {
  const turn = active
  if (pending && turn?.turnId) {
    void deps.dispatch(nlpApi.endpoints.agentCancel.initiate({ agentId: turn.agentId, turnId: turn.turnId }))
  }
  clearActiveInlineTurn()
}

const subscribe = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getSnapshot = (): ActiveInlineTurn | null => active

/** Subscribe to the active inline turn. `null` when no inline turn is in flight. */
export const useActiveInlineTurn = (): ActiveInlineTurn | null =>
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
